import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import TvOutlinedIcon from "@mui/icons-material/TvOutlined";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import { tokens } from "../theme";
import type { SignageScreenGroup } from "@shared/types/signage";

/** The rows the picker lists - the minimum a screen needs to be chosen by. */
export interface GroupPickerScreen {
  id: string;
  /** What somebody calls it - room number, or the model as a last resort. */
  label: string;
  /** The screen's own id, shown under the label so two "Reception"s differ. */
  code: string;
  /** Which location it is in. Blank for a scope that has only one. */
  premiseLabel: string;
}

/** Longer than any name anybody needs, and short enough to fit a table cell. */
const NAME_LIMIT = 100;

const ALL_LOCATIONS = "__all__";

/**
 * Make or edit a screen group.
 *
 * ## Why the chosen screens sit ABOVE the list
 *
 * The list is long and filtered; the selection is the thing being built. Once
 * the list scrolls, a selection rendered only as ticks inside it is invisible -
 * you cannot answer "what have I picked" without scrolling back through
 * everything. So the selection is its own summary, with a chip per screen that
 * can be removed from there, and the ticks in the list stay as the second view
 * of the same fact.
 *
 * ## Why it shows which groups a screen is already in
 *
 * Membership is many-to-many on purpose, so "is this screen already covered"
 * is a real question with a non-obvious answer, and it is the one an operator
 * asks while building the third group over the same wall.
 */
export default function SignageGroupDialog({
  open,
  editing,
  screens,
  groups,
  saving,
  error,
  onClose,
  onSave,
}: {
  open: boolean;
  /** The group being edited, or null to make a new one. */
  editing: SignageScreenGroup | null;
  screens: GroupPickerScreen[];
  /** Every group in scope, for the "already in" column. */
  groups: SignageScreenGroup[];
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (input: { name: string; screenIds: string[] }) => void;
}) {
  const [name, setName] = useState("");
  const [chosen, setChosen] = useState<string[]>([]);
  const [query, setQuery] = useState("");
  const [premise, setPremise] = useState(ALL_LOCATIONS);

  useEffect(() => {
    if (!open) return;
    setName(editing?.name ?? "");
    setChosen(editing?.screenIds ?? []);
    setQuery("");
    setPremise(ALL_LOCATIONS);
  }, [open, editing]);

  const premises = useMemo(
    () =>
      [...new Set(screens.map((s) => s.premiseLabel).filter(Boolean))].sort(
        (a, b) => a.localeCompare(b)
      ),
    [screens]
  );

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return screens.filter(
      (s) =>
        (premise === ALL_LOCATIONS || s.premiseLabel === premise) &&
        (!q ||
          s.label.toLowerCase().includes(q) ||
          s.code.toLowerCase().includes(q))
    );
  }, [screens, query, premise]);

  /** screenId -> the groups it is already in. Built once, not per row. */
  const memberships = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const g of groups) {
      if (editing && g.id === editing.id) continue;
      for (const id of g.screenIds) {
        const list = map.get(id);
        if (list) list.push(g.name);
        else map.set(id, [g.name]);
      }
    }
    return map;
  }, [groups, editing]);

  const byId = useMemo(() => new Map(screens.map((s) => [s.id, s])), [screens]);

  const toggle = (id: string) =>
    setChosen((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{ sx: { maxWidth: 820 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="flex-start" gap={1.75}>
          <Box
            sx={{
              width: 44,
              height: 44,
              flexShrink: 0,
              borderRadius: "10px",
              bgcolor: tokens.brandTintSoft,
              display: "grid",
              placeItems: "center",
              color: tokens.brand,
            }}
          >
            <CollectionsOutlinedIcon />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
              {editing ? "Edit screen group" : "New screen group"}
            </Typography>
            <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
              Choose which screens belong to the group.
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.75 }}>
          Group name
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Name the group…"
          value={name}
          onChange={(e) => setName(e.target.value.slice(0, NAME_LIMIT))}
          inputProps={{ "aria-label": "Group name", maxLength: NAME_LIMIT }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Typography sx={{ fontSize: 12, color: tokens.inkFaint }}>
                  {name.length} / {NAME_LIMIT}
                </Typography>
              </InputAdornment>
            ),
          }}
        />

        {/* -- what has been chosen so far -------------------------------- */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mt: 3, mb: 1 }}
        >
          <Typography sx={{ fontWeight: 700 }}>
            Selected screens ({chosen.length})
          </Typography>
          {chosen.length > 0 && (
            <Button size="small" onClick={() => setChosen([])}>
              Clear selection
            </Button>
          )}
        </Stack>

        {chosen.length === 0 ? (
          <Stack
            direction="row"
            alignItems="center"
            gap={2}
            sx={{
              border: `1px solid ${tokens.divider}`,
              borderRadius: "10px",
              p: 2.5,
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                bgcolor: tokens.brandTintSoft,
                color: tokens.brand,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              <TvOutlinedIcon />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                No screens selected
              </Typography>
              <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
                Pick screens from the list below to add them to the group.
              </Typography>
            </Box>
          </Stack>
        ) : (
          <Box
            sx={{
              border: `1px solid ${tokens.divider}`,
              borderRadius: "10px",
              p: 1.5,
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {chosen.map((id) => (
              <Chip
                key={id}
                size="small"
                icon={<TvOutlinedIcon />}
                /* A chosen screen the list no longer offers is still a member.
                   Naming it by id rather than dropping it silently is the only
                   way an operator can see that something moved. */
                label={byId.get(id)?.label ?? id}
                onDelete={() => toggle(id)}
                sx={{ bgcolor: tokens.brandTintSoft }}
              />
            ))}
          </Box>
        )}

        {/* -- the list to choose from ------------------------------------ */}
        <Typography sx={{ fontWeight: 700, mt: 3, mb: 1.5 }}>
          Choose screens
        </Typography>
        <Stack direction="row" gap={1.5} sx={{ mb: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search screens…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            inputProps={{ "aria-label": "Search screens" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {premises.length > 1 && (
            <TextField
              select
              size="small"
              value={premise}
              onChange={(e) => setPremise(e.target.value)}
              inputProps={{ "aria-label": "Location" }}
              sx={{ minWidth: 190 }}
            >
              <MenuItem value={ALL_LOCATIONS}>All locations</MenuItem>
              {premises.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Stack>

        <Box
          sx={{
            border: `1px solid ${tokens.divider}`,
            borderRadius: "10px",
            maxHeight: 320,
            overflowY: "auto",
          }}
        >
          {shown.length === 0 ? (
            <Typography
              sx={{
                p: 3,
                textAlign: "center",
                fontSize: 13,
                color: tokens.inkMuted,
              }}
            >
              No screen matches that.
            </Typography>
          ) : (
            shown.map((s, i) => {
              const inGroups = memberships.get(s.id) ?? [];
              return (
                <Stack
                  key={s.id}
                  direction="row"
                  alignItems="center"
                  gap={1.5}
                  onClick={() => toggle(s.id)}
                  sx={{
                    px: 1.5,
                    py: 1,
                    cursor: "pointer",
                    borderTop: i === 0 ? "none" : `1px solid ${tokens.divider}`,
                    "&:hover": { bgcolor: tokens.rowAlt },
                  }}
                >
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: "8px",
                      bgcolor: tokens.brandTintSoft,
                      color: tokens.brand,
                      display: "grid",
                      placeItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    <TvOutlinedIcon fontSize="small" />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap sx={{ fontSize: 14, fontWeight: 600 }}>
                      {s.label}
                    </Typography>
                    <Typography
                      noWrap
                      sx={{ fontSize: 12, color: tokens.inkMuted }}
                    >
                      {s.code}
                      {s.premiseLabel ? ` · ${s.premiseLabel}` : ""}
                    </Typography>
                  </Box>
                  {/* Membership is many-to-many, so "already covered" is a real
                      question - and the one asked while building a third group
                      over the same wall. */}
                  <Stack direction="row" gap={0.5} sx={{ flexShrink: 0 }}>
                    {inGroups.slice(0, 2).map((g) => (
                      <Chip
                        key={g}
                        label={g}
                        size="small"
                        sx={{ bgcolor: tokens.rowAlt, fontSize: 11 }}
                      />
                    ))}
                    {inGroups.length > 2 && (
                      <Chip
                        label={`+${inGroups.length - 2}`}
                        size="small"
                        sx={{ bgcolor: tokens.rowAlt, fontSize: 11 }}
                      />
                    )}
                  </Stack>
                  <Checkbox
                    checked={chosen.includes(s.id)}
                    // The row is the control; the box would otherwise toggle
                    // twice on a click that lands on it.
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggle(s.id)}
                    inputProps={{ "aria-label": `Select ${s.label}` }}
                  />
                </Stack>
              );
            })
          )}
        </Box>

        {error && (
          <Typography sx={{ mt: 2, fontSize: 13, color: tokens.error }}>
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          disabled={saving || !name.trim()}
          onClick={() => onSave({ name: name.trim(), screenIds: chosen })}
        >
          {saving ? "Saving…" : "Save screen group"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
